import { createAction, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Equipment, HarmonicFilterDto, HarmonicFilterInstallationType, SurfaceDto, SwitchBoardDto } from "../../model/calculations/calculation"

export interface State {
    loading: boolean;
    error: string | null;
    VSDList: SurfaceDto[],
    SwitchBoardList: SwitchBoardDto[],
    KVA: number | null,
    KW: number | null,
    driveAmps: number | null,
    supplyVoltage: string,
    BasicElectrictyCost: string,
    SurfaceCalculationComplete: boolean,
    HarmonicFilter: {
        InstallationType: HarmonicFilterInstallationType | null,
        EstimatedCurrentAmps: number | null,
        RecommendedFilters: {
            SingleStage: HarmonicFilterDto | null,
            DualStage: HarmonicFilterDto | null
        },
        FilterSelection: HarmonicFilterDto | null
    },
    HarmonicFilterList: HarmonicFilterDto[],
    Equipments: any[],
    PowerField: number | null,
    Transformer: Equipment[] | any[],
    ESPEquipments: Equipment[],
    WeightField: string,
    SelectedTransformer: Equipment | null,
    FetchSwitchControllerLoad: any | null,
    FetchVSDControllerLoad: any | null,
    SelectedVSD: any | null,
    SelectedSwitchBoard: any | null,
    enableReport: boolean;
}

export const initialState: State = {
    loading: false,
    error: null,
    VSDList: [],
    SwitchBoardList: [],
    KVA: 0,
    KW: 0,
    driveAmps: 0,
    supplyVoltage: '',
    BasicElectrictyCost: '',
    SurfaceCalculationComplete: false,
    HarmonicFilter: {
        InstallationType: null,
        EstimatedCurrentAmps: null,
        RecommendedFilters: {
            SingleStage: null,
            DualStage: null
        },
        FilterSelection: null
    },
    HarmonicFilterList: [],
    Equipments: [],
    PowerField: null,
    Transformer: [],
    ESPEquipments: [],
    WeightField: '',
    SelectedTransformer: null,
    FetchSwitchControllerLoad: null,
    FetchVSDControllerLoad: null,
    SelectedVSD: null,
    SelectedSwitchBoard: null,
    enableReport: false,
}


export const fetchEquipments = createAction("surface/fetchEquipments");
export const fetchSwitchControllerLoad = createAction("surface/fetchSwitchControllerLoad");
export const fetchTransformers = createAction("surface/fetchTransformers");


const surfaceEquipmentSlice = createSlice({
    name: "surface",
    initialState,
    reducers: {
        resetSurfaceEquipmentOutput: () => initialState,
        setSurfaceVSDList: (state, action: PayloadAction<any>) => {
            state.VSDList = action.payload.data;
        },
        setSurfaceSwitchBoardList: (state, action) => {
            state.SwitchBoardList = action.payload.payload;
        },
        setSurfaceKvaAndKw: (state, action) => {
            state.KVA = action.payload.kva;
            state.KW = action.payload.kw;
        },
        setSurfaceDriveAmps: (state, action) => {
            state.driveAmps = action.payload;
        },
        setSurfaceSupplyVoltage: (state, action) => {
            state.supplyVoltage = action.payload;
        },
        setEquipment: (state, action) => {
            state.Equipments = action.payload;
        },
        setSurfaceBasicElectricityCose: (state, action) => {
            state.BasicElectrictyCost = action.payload;
        },
        setSurfaceCalculationComplete: (state, action) => {
            state.SurfaceCalculationComplete = action.payload;
        },
        setHfInstallationType: (
            state,
            action: PayloadAction<{
                estimatedCurrentAmps: number;
                installationType: HarmonicFilterInstallationType;

            }>
        ) => {
            state.HarmonicFilter.InstallationType = action.payload?.installationType;
            state.HarmonicFilter.EstimatedCurrentAmps = action.payload?.estimatedCurrentAmps;
        },
        setHfEstimatedCurrentAmps: (state, action: PayloadAction<number>) => {
            state.HarmonicFilter.EstimatedCurrentAmps = action.payload;
        },
        setHfFilterSelection: (state, action: PayloadAction<HarmonicFilterDto>) => {
            state.HarmonicFilter.FilterSelection = action.payload;
        },
        setHfRecommendedFilterAndSelectionList: (
            state,
            action: PayloadAction<{

                recommendedFilters: {
                    singleStage: HarmonicFilterDto | null,
                    dualStage: HarmonicFilterDto | null
                },
                filters: HarmonicFilterDto[];
            }>
        ) => {
            state.HarmonicFilter.RecommendedFilters.SingleStage = action.payload?.recommendedFilters?.singleStage;
            state.HarmonicFilter.RecommendedFilters.DualStage = action.payload?.recommendedFilters?.dualStage;
            state.HarmonicFilterList = action?.payload?.filters && action?.payload?.filters?.map((filter) => ({
                ...filter,
                label: filter?.name,
                isHighlight: action?.payload?.recommendedFilters?.singleStage?.id === filter?.id || action?.payload?.recommendedFilters?.dualStage?.id === filter?.id ? true : false
            }));
        },
        setPowerField: (state, action: PayloadAction<number>) => {
            state.PowerField = action.payload;
        },
        setWeightField: (state, action: PayloadAction<string>) => {
            state.WeightField = action.payload;
        },
        setEspEquipments: (state, action) => {
            state.ESPEquipments = action.payload;
        },
        setSelectedTransformer: (state, action) => {
            state.SelectedTransformer = action.payload?.[0];
        },
        setFetchSwitchControllerLoad: (state, action: PayloadAction<number>) => {
            state.FetchSwitchControllerLoad = action.payload;
        },
        setFetchVSDControllerLoad: (state, action: PayloadAction<number>) => {
            state.FetchVSDControllerLoad = action.payload;
        },
        setSelectedVSD: (state, action) => {
            state.SelectedVSD = action.payload;
        },
        setSelectedSwitchBoard: (state, action) => {
            state.SelectedSwitchBoard = action.payload;
        },
        setTranformerList: (state, action) => {
            state.Transformer = action.payload;
        },
        setEnableReport: (state, action) => {
            state.enableReport = action?.payload;
        },
    },
});

export const {
    resetSurfaceEquipmentOutput,
    setSurfaceVSDList,
    setSurfaceSwitchBoardList,
    setSurfaceKvaAndKw,
    setSurfaceDriveAmps,
    setSurfaceSupplyVoltage,
    setEquipment,
    setSurfaceBasicElectricityCose,
    setSurfaceCalculationComplete,
    setHfInstallationType,
    setHfEstimatedCurrentAmps,
    setHfFilterSelection,
    setHfRecommendedFilterAndSelectionList,
    setPowerField,
    setWeightField,
    setTranformerList,
    setEspEquipments,
    setSelectedTransformer,
    setFetchSwitchControllerLoad,
    setFetchVSDControllerLoad,
    setSelectedVSD,
    setSelectedSwitchBoard,
    setEnableReport,
} = surfaceEquipmentSlice.actions;

export default surfaceEquipmentSlice.reducer;